// The WebGL renderer: canvas size and pixel ratio, static shadow map, context loss and per-frame stats.
import { PCFShadowMap, SRGBColorSpace, WebGLRenderer, type Camera, type Scene } from 'three';

export type Resolution = 'baja' | 'media' | 'alta';

/** Device pixels per CSS pixel for each resolution setting, never above the screen's own. */
export function pixelRatioFor(resolution: Resolution, devicePixelRatio: number): number {
  const target = resolution === 'baja' ? 1 : resolution === 'media' ? 1.5 : 2;
  return Math.max(1, Math.min(target, devicePixelRatio));
}

/** A frame the GPU has not finished after this long is not waited for any more (a lost fence). */
const MAX_GPU_WAIT_MS = 1000;

export interface FrameStats {
  /** Draw calls of the last frame, including the shadow pass when it was redrawn. */
  readonly calls: number;
  readonly triangles: number;
  /** CPU time spent issuing the last frame, in milliseconds. */
  readonly cpuMs: number;
  readonly geometries: number;
  readonly textures: number;
  readonly shadowRedrawn: boolean;
}

export interface StageOptions {
  readonly canvas: HTMLCanvasElement;
  readonly onContextLost: () => void;
  readonly onContextRestored: () => void;
}

export class Stage {
  readonly renderer: WebGLRenderer;
  private width = 1;
  private height = 1;
  private resolutionValue: Resolution = 'alta';
  private lastStats: FrameStats = { calls: 0, triangles: 0, cpuMs: 0, geometries: 0, textures: 0, shadowRedrawn: false };
  /** Signals when the GPU has finished the last frame drawn. */
  private fence: WebGLSync | null = null;
  private fencedAt = 0;

  constructor(opts: StageOptions) {
    this.renderer = new WebGLRenderer({ canvas: opts.canvas, antialias: true, powerPreference: 'high-performance' });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFShadowMap;
    // The sun and the island do not move: the shadow map is drawn only when the scene changes.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.info.autoReset = false;
    opts.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      // The fence died with the context.
      this.fence = null;
      opts.onContextLost();
    });
    opts.canvas.addEventListener('webglcontextrestored', () => {
      this.renderer.shadowMap.needsUpdate = true;
      opts.onContextRestored();
    });
  }

  get size(): { readonly width: number; readonly height: number } {
    return { width: this.width, height: this.height };
  }

  get pixelRatio(): number {
    return this.renderer.getPixelRatio();
  }

  get stats(): FrameStats {
    return this.lastStats;
  }

  setResolution(resolution: Resolution): void {
    this.resolutionValue = resolution;
    this.renderer.setPixelRatio(pixelRatioFor(resolution, window.devicePixelRatio || 1));
    this.renderer.setSize(this.width, this.height, false);
  }

  resize(width: number, height: number): void {
    this.width = Math.max(1, Math.round(width));
    this.height = Math.max(1, Math.round(height));
    this.renderer.setPixelRatio(pixelRatioFor(this.resolutionValue, window.devicePixelRatio || 1));
    this.renderer.setSize(this.width, this.height, false);
  }

  /** Asks for the shadow map to be redrawn on the next frame (after the scene content changed). */
  invalidateShadows(): void {
    this.renderer.shadowMap.needsUpdate = true;
  }

  setShadows(on: boolean): void {
    this.renderer.shadowMap.enabled = on;
    this.renderer.shadowMap.needsUpdate = true;
  }

  /** True while the GPU is still drawing the last frame (see FrameScheduler's gpuBusy). */
  get gpuBusy(): boolean {
    const fence = this.fence;
    if (!fence) return false;
    const gl = this.renderer.getContext() as WebGL2RenderingContext;
    // A lost context answers null rather than UNSIGNALED: nothing to wait for then.
    const busy = gl.getSyncParameter(fence, gl.SYNC_STATUS) === gl.UNSIGNALED && performance.now() - this.fencedAt < MAX_GPU_WAIT_MS;
    if (!busy) {
      gl.deleteSync(fence);
      this.fence = null;
    }
    return busy;
  }

  render(scene: Scene, camera: Camera): void {
    const shadowRedrawn = this.renderer.shadowMap.enabled && this.renderer.shadowMap.needsUpdate;
    const info = this.renderer.info;
    info.reset();
    const start = performance.now();
    this.renderer.render(scene, camera);
    const end = performance.now();
    const cpuMs = end - start;
    const gl = this.renderer.getContext() as WebGL2RenderingContext;
    if (this.fence) gl.deleteSync(this.fence);
    this.fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    this.fencedAt = end;
    // Send the frame and its fence to the GPU now, so the fence can signal.
    gl.flush();
    this.lastStats = {
      calls: info.render.calls,
      triangles: info.render.triangles,
      cpuMs,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      shadowRedrawn,
    };
  }
}
